import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  CallSplit as CallSplitIcon,
} from '@mui/icons-material';
import { Party } from '../../types';
import { formatPartyLabel, formatPartyLabelById } from '../../utils/partyDisplay';
import { getZoomCompensatedAutocompleteSlotProps } from '../../utils/zoomCompensation';
import { RevisionWorkflowFormValues } from './formTypes';

type WorkflowMode = 'sequential' | 'parallel';

export interface RevisionWorkflowFormProps {
  value: RevisionWorkflowFormValues;
  onChange: (value: RevisionWorkflowFormValues) => void;
  parties: Party[];
  invalid?: (key: string) => boolean;
  onFieldEdit?: (key: string) => void;
}

const RevisionWorkflowForm: React.FC<RevisionWorkflowFormProps> = ({
  value,
  onChange,
  parties,
  invalid,
  onFieldEdit,
}) => {
  const [selectedCheckers, setSelectedCheckers] = useState<Party[]>([]);
  const [selectedApprovers, setSelectedApprovers] = useState<Party[]>([]);
  const [checkerWorkflowMode, setCheckerWorkflowMode] = useState<WorkflowMode>('sequential');
  const [approverWorkflowMode, setApproverWorkflowMode] = useState<WorkflowMode>('sequential');

  const patch = (partial: Partial<RevisionWorkflowFormValues>) => {
    onChange({ ...value, ...partial });
  };

  const getPartyName = (partyId: number) => formatPartyLabelById(partyId, parties);

  const handleAddCheckers = () => {
    if (selectedCheckers.length === 0) return;

    if (checkerWorkflowMode === 'parallel') {
      const nextStepOrder =
        value.checkers.length > 0 ? Math.max(...value.checkers.map((c) => c.step_order)) + 1 : 1;
      const newCheckers = selectedCheckers.map((checker) => ({
        party_id: checker.id,
        step_order: nextStepOrder,
      }));
      patch({ checkers: [...value.checkers, ...newCheckers] });
    } else {
      let nextStepOrder =
        value.checkers.length > 0 ? Math.max(...value.checkers.map((c) => c.step_order)) + 1 : 1;
      const newCheckers = selectedCheckers.map((checker) => ({
        party_id: checker.id,
        step_order: nextStepOrder++,
      }));
      patch({ checkers: [...value.checkers, ...newCheckers] });
    }
    setSelectedCheckers([]);
    onFieldEdit?.('checkers');
  };

  const handleRemoveChecker = (index: number) => {
    const newCheckers = value.checkers.filter((_, i) => i !== index);
    const stepOrderMap = new Map<number, number>();
    let currentStep = 1;
    newCheckers.forEach((checker) => {
      if (!stepOrderMap.has(checker.step_order)) {
        stepOrderMap.set(checker.step_order, currentStep++);
      }
    });
    const reorderedCheckers = newCheckers.map((checker) => ({
      ...checker,
      step_order: stepOrderMap.get(checker.step_order)!,
    }));
    patch({ checkers: reorderedCheckers });
  };

  const handleAddApprovers = () => {
    if (selectedApprovers.length === 0) return;

    const maxCheckerStep =
      value.checkers.length > 0 ? Math.max(...value.checkers.map((c) => c.step_order)) : 0;

    if (approverWorkflowMode === 'parallel') {
      const nextStepOrder =
        value.approvers.length > 0
          ? Math.max(...value.approvers.map((a) => a.step_order)) + 1
          : maxCheckerStep + 1;
      const newApprovers = selectedApprovers.map((approver) => ({
        party_id: approver.id,
        step_order: nextStepOrder,
      }));
      patch({ approvers: [...value.approvers, ...newApprovers] });
    } else {
      let nextStepOrder =
        value.approvers.length > 0
          ? Math.max(...value.approvers.map((a) => a.step_order)) + 1
          : maxCheckerStep + 1;
      const newApprovers = selectedApprovers.map((approver) => ({
        party_id: approver.id,
        step_order: nextStepOrder++,
      }));
      patch({ approvers: [...value.approvers, ...newApprovers] });
    }
    setSelectedApprovers([]);
    onFieldEdit?.('approvers');
  };

  const handleRemoveApprover = (index: number) => {
    const newApprovers = value.approvers.filter((_, i) => i !== index);
    const maxCheckerStep =
      value.checkers.length > 0 ? Math.max(...value.checkers.map((c) => c.step_order)) : 0;
    const stepOrderMap = new Map<number, number>();
    let currentStep = maxCheckerStep + 1;
    newApprovers.forEach((approver) => {
      if (!stepOrderMap.has(approver.step_order)) {
        stepOrderMap.set(approver.step_order, currentStep++);
      }
    });
    const reorderedApprovers = newApprovers.map((approver) => ({
      ...approver,
      step_order: stepOrderMap.get(approver.step_order)!,
    }));
    patch({ approvers: reorderedApprovers });
  };

  const groupedCheckers = value.checkers.reduce(
    (acc, checker) => {
      if (!acc[checker.step_order]) {
        acc[checker.step_order] = [];
      }
      acc[checker.step_order].push(checker);
      return acc;
    },
    {} as Record<number, typeof value.checkers>
  );

  const groupedApprovers = value.approvers.reduce(
    (acc, approver) => {
      if (!acc[approver.step_order]) {
        acc[approver.step_order] = [];
      }
      acc[approver.step_order].push(approver);
      return acc;
    },
    {} as Record<number, typeof value.approvers>
  );

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={parties.filter((p) => !value.checkers.some((c) => c.party_id === p.id))}
          getOptionLabel={(option) => formatPartyLabel(option)}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          value={parties.find((p) => p.id === value.author_party_id) || null}
          onChange={(_, newValue) => patch({ author_party_id: newValue?.id })}
          slotProps={getZoomCompensatedAutocompleteSlotProps()}
          renderInput={(params) => (
            <TextField {...params} label="Originator" size="small" />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Checkers
        </Typography>
        <Box display="flex" gap={1} mb={2} alignItems="flex-start">
          <Box flex={1}>
            <Autocomplete
              multiple
              options={parties.filter(
                (p) =>
                  !value.checkers.some((c) => c.party_id === p.id) &&
                  p.id !== value.author_party_id
              )}
              getOptionLabel={(option) => formatPartyLabel(option)}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              value={selectedCheckers}
              onChange={(_, newValue) => setSelectedCheckers(newValue)}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Checkers"
                  size="small"
                  error={Boolean(invalid?.('checkers'))}
                  onFocus={() => onFieldEdit?.('checkers')}
                />
              )}
            />
          </Box>
          <ToggleButtonGroup
            value={checkerWorkflowMode}
            exclusive
            onChange={(_, newMode) => newMode && setCheckerWorkflowMode(newMode)}
            size="small"
          >
            <ToggleButton value="sequential" aria-label="sequential">
              <ArrowForwardIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="parallel" aria-label="parallel">
              <CallSplitIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCheckers}
            disabled={selectedCheckers.length === 0}
            sx={{ minWidth: '100px' }}
          >
            Add
          </Button>
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          {Object.entries(groupedCheckers).map(([stepOrder, checkersInStep]) => (
            <Box key={stepOrder} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ minWidth: '30px', fontWeight: 600 }}>
                {stepOrder}.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} flex={1}>
                {checkersInStep.map((checker) => {
                  const globalIndex = value.checkers.findIndex((c) => c === checker);
                  return (
                    <Chip
                      key={globalIndex}
                      label={getPartyName(checker.party_id)}
                      onDelete={() => handleRemoveChecker(globalIndex)}
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
              {checkersInStep.length > 1 && <Chip label="Parallel" size="small" color="info" />}
            </Box>
          ))}
          {value.checkers.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No checkers added yet
            </Typography>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Approvers
        </Typography>
        <Box display="flex" gap={1} mb={2} alignItems="flex-start">
          <Box flex={1}>
            <Autocomplete
              multiple
              options={parties.filter((p) => !value.approvers.some((a) => a.party_id === p.id))}
              getOptionLabel={(option) => formatPartyLabel(option)}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              value={selectedApprovers}
              onChange={(_, newValue) => setSelectedApprovers(newValue)}
              slotProps={getZoomCompensatedAutocompleteSlotProps()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Approvers"
                  size="small"
                  error={Boolean(invalid?.('approvers'))}
                  onFocus={() => onFieldEdit?.('approvers')}
                />
              )}
            />
          </Box>
          <ToggleButtonGroup
            value={approverWorkflowMode}
            exclusive
            onChange={(_, newMode) => newMode && setApproverWorkflowMode(newMode)}
            size="small"
          >
            <ToggleButton value="sequential" aria-label="sequential">
              <ArrowForwardIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="parallel" aria-label="parallel">
              <CallSplitIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddApprovers}
            disabled={selectedApprovers.length === 0}
            sx={{ minWidth: '100px' }}
          >
            Add
          </Button>
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          {Object.entries(groupedApprovers).map(([stepOrder, approversInStep]) => (
            <Box key={stepOrder} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ minWidth: '30px', fontWeight: 600 }}>
                {stepOrder}.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} flex={1}>
                {approversInStep.map((approver) => {
                  const globalIndex = value.approvers.findIndex((a) => a === approver);
                  return (
                    <Chip
                      key={globalIndex}
                      label={getPartyName(approver.party_id)}
                      onDelete={() => handleRemoveApprover(globalIndex)}
                      color="secondary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
              {approversInStep.length > 1 && <Chip label="Parallel" size="small" color="info" />}
            </Box>
          ))}
          {value.approvers.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No approvers added yet
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default RevisionWorkflowForm;
